import { Component } from '@theme/component';
import { QuickAddComponent } from '@theme/quick-add';
import { isClickedOutside, isMobileBreakpoint, isTouchDevice, mediaQueryLarge } from '@theme/utilities';

/**
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLButtonElement} trigger
 * @property {HTMLAnchorElement} productLink
 *
 * @extends Component<Refs>
 */

export class ProductHotspotComponent extends Component {
  requiredRefs = ['trigger', 'dialog'];
  /** @type {(() => void) | null} */
  #pointerenterHandler = null;
  timer = /** @type {number | null} */ (null);

  connectedCallback() {
    super.connectedCallback();

    this.#handleBreakpointChange();
    mediaQueryLarge.addEventListener('change', this.#handleBreakpointChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#removeDesktopListeners();
    mediaQueryLarge.removeEventListener('change', this.#handleBreakpointChange);
  }

  #openQuickAddModal() {
    const quickAddComponent = /** @type {QuickAddComponent | null} */ (this.querySelector('quick-add-component'));

    if (!quickAddComponent) return;
    quickAddComponent.handleClick(new MouseEvent('click', { bubbles: true, cancelable: true }));
  }

  #setupDesktopListeners() {
    const { trigger, dialog } = this.refs;

    /** @type {() => void} */
    const pointerenterHandler = () => {
      if (dialog.open) return;

      this.timer = setTimeout(() => {
        this.showDialog();
      }, 120);
      trigger.addEventListener('pointerleave', this.#handlePointerLeave);
    };

    this.#pointerenterHandler = pointerenterHandler;
    trigger.addEventListener('pointerenter', pointerenterHandler);
  }

  #removeDesktopListeners() {
    const { trigger } = this.refs;

    if (this.#pointerenterHandler) {
      trigger.removeEventListener('pointerenter', this.#pointerenterHandler);
      trigger.removeEventListener('pointerleave', this.#handlePointerLeave);
      this.#pointerenterHandler = null;
    }

    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  #handleBreakpointChange = () => {
    this.#removeDesktopListeners();

    if (!isMobileBreakpoint()) {
      this.#setupDesktopListeners();
    }
  };

  #calculateDialogPlacement() {
    const { trigger, dialog } = this.refs;

    const hotspotsContainer = this.parentElement;

    if (!hotspotsContainer) {
      return;
    }

    const BUTTON_GAP = 10;
    const CONTAINER_GAP = 10;
    const VIEWPORT_GAP = 16;
    const TOTAL_GAP = BUTTON_GAP + CONTAINER_GAP;

    const containerRect = hotspotsContainer?.getBoundingClientRect();

    const triggerRect = trigger.getBoundingClientRect();

    const viewportH = window.innerHeight;

    dialog.style.visibility = 'hidden';
    dialog.style.display = 'block';
    dialog.style.transform = 'none';
    dialog.removeAttribute('data-placement');

    const { width: dialogWidth, height: dialogHeight } = dialog.getBoundingClientRect();

    dialog.style.removeProperty('display');
    dialog.style.removeProperty('visibility');
    dialog.style.removeProperty('transform');

    const buttonLeft = triggerRect.left - containerRect.left;
    const buttonRight = triggerRect.right - containerRect.left;
    const buttonTop = triggerRect.top - containerRect.top;
    const buttonBottom = triggerRect.bottom - containerRect.top;

    const spaceRight = containerRect.width - buttonRight - CONTAINER_GAP;
    const spaceLeft = buttonLeft - CONTAINER_GAP;

    let x = 'right';

    if (spaceRight >= dialogWidth + BUTTON_GAP) {
      x = 'right';
    } else if (spaceLeft >= dialogWidth + BUTTON_GAP) {
      x = 'left';
    } else {
      x = 'center';
    }

    let y = 'bottom';
    let verticalOffset = 0;

    if (x !== 'center') {
      let dialogStartY = buttonTop;
      let dialogEndY = buttonTop + dialogHeight;

      if (dialogEndY > containerRect.height - CONTAINER_GAP) {
        dialogStartY = buttonBottom - dialogHeight;
        dialogEndY = buttonBottom;
        y = 'top';

        if (dialogStartY < CONTAINER_GAP) {
          verticalOffset = CONTAINER_GAP - dialogStartY;
        } else if (dialogEndY > containerRect.height - CONTAINER_GAP) {
          verticalOffset = -(dialogEndY - (containerRect.height - CONTAINER_GAP));
        }
      } else {
        if (dialogStartY < CONTAINER_GAP) {
          if (dialogStartY < CONTAINER_GAP) {
            verticalOffset = CONTAINER_GAP - dialogStartY;
          }
          y = 'bottom';
        }
      }

      /*
        Final viewport check: regardless of container fit, nudge the dialog so its top stays
        below the viewport top and its bottom stays above the viewport bottom (with a margin).
        Container is in mood-board cells which can be partially scrolled out of view.
      */
      const containerOriginY = containerRect.top + window.scrollY;
      const dialogTopViewport = containerOriginY + dialogStartY + verticalOffset - window.scrollY;
      const dialogBottomViewport = dialogTopViewport + dialogHeight;
      if (dialogTopViewport < VIEWPORT_GAP) {
        verticalOffset += VIEWPORT_GAP - dialogTopViewport;
      } else if (dialogBottomViewport > viewportH - VIEWPORT_GAP) {
        verticalOffset -= dialogBottomViewport - (viewportH - VIEWPORT_GAP);
      }
    } else {
      if (containerRect.height - buttonBottom >= dialogHeight + TOTAL_GAP) {
        y = 'bottom';
      } else if (buttonTop >= dialogHeight + TOTAL_GAP) {
        y = 'top';
      } else {
        y = buttonTop < containerRect.height / 2 ? 'bottom' : 'top';
      }
    }

    dialog.dataset.placement = `${x},${y}`;

    if (verticalOffset !== 0) {
      dialog.style.setProperty('--dialog-vertical-offset', `${verticalOffset}px`);
    } else {
      dialog.style.removeProperty('--dialog-vertical-offset');
    }

    return new Promise((resolve) => setTimeout(resolve, 100));
  }

  #handlePointerLeave = (e) => {
    const { dialog, trigger } = this.refs;

    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }

    if (!dialog.open) return;

    const isLeavingTrigger = e.target === trigger;
    const isLeavingDialog = e.target === dialog;
    const isGoingToDialog =
      e.relatedTarget === dialog ||
      (e.relatedTarget instanceof Element && e.relatedTarget.closest('dialog') === dialog);
    const isGoingToTrigger = e.relatedTarget === trigger;

    if ((isLeavingTrigger && !isGoingToDialog) || (isLeavingDialog && !isGoingToTrigger)) {
      this.closeDialog();
    }
  };

  getHotspotProductLink() {
    return this.refs.productLink || null;
  }

  #isEarlyAccess() {
    return this.dataset.moodHotspotEarlyAccess === 'true';
  }

  handleHotspotClick = (e) => {
    if (isMobileBreakpoint() || isTouchDevice()) {
      e.preventDefault();
      e.stopPropagation();
      if (this.#isEarlyAccess()) {
        this.showMobileSheet();
      } else {
        this.#openQuickAddModal();
      }
    } else {
      this.showDialog();
    }
  };

  showDialog = async () => {
    const { dialog } = this.refs;
    await this.#calculateDialogPlacement();
    dialog.dataset.showing = 'true';
    dialog.show();
    document.body.addEventListener('click', this.lightDismissMouse);
    document.body.addEventListener('keydown', this.lightDismissKeyboard);
    document.body.addEventListener('keyup', this.lightDismissKeyboard);
    dialog.addEventListener('pointerleave', this.#handlePointerLeave);
    window.addEventListener('resize', this.#handleViewportChange, { passive: true });
  };

  #handleViewportChange = () => {
    const { dialog } = this.refs;
    if (!dialog.open) return;
    this.#calculateDialogPlacement();
  };

  showMobileSheet = () => {
    const { dialog } = this.refs;
    if (dialog.open) return;
    dialog.setAttribute('data-mood-mobile-sheet', 'true');
    dialog.dataset.showing = 'true';
    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.show();
    }
    dialog.addEventListener('click', this.#handleSheetBackdropClick);
    dialog.addEventListener('cancel', this.#handleSheetCancel);
  };

  #handleSheetBackdropClick = (event) => {
    const { dialog } = this.refs;
    if (event.target === dialog) {
      this.closeDialog();
    }
  };

  #handleSheetCancel = (event) => {
    event.preventDefault();
    this.closeDialog();
  };

  closeDialog = async () => {
    const { dialog, trigger } = this.refs;
    dialog.dataset.closing = 'true';
    dialog.close();
    document.body.removeEventListener('click', this.lightDismissMouse);
    document.body.removeEventListener('keydown', this.lightDismissKeyboard);
    document.body.removeEventListener('keyup', this.lightDismissKeyboard);
    dialog.removeEventListener('pointerleave', this.#handlePointerLeave);
    dialog.removeEventListener('click', this.#handleSheetBackdropClick);
    dialog.removeEventListener('cancel', this.#handleSheetCancel);
    trigger.removeEventListener('pointerleave', this.#handlePointerLeave);
    window.removeEventListener('resize', this.#handleViewportChange);
    const animations = dialog.getAnimations({ subtree: true });
    await Promise.allSettled(animations.map((a) => a.finished));
    if (!dialog.open) {
      delete dialog.dataset.showing;
      delete dialog.dataset.closing;
      delete dialog.dataset.placement;
      dialog.removeAttribute('data-mood-mobile-sheet');
    }
  };

  lightDismissMouse = (event) => {
    const { dialog } = this.refs;
    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  };

  lightDismissKeyboard = (event) => {
    const { dialog } = this.refs;
    if (
      (event.type === 'keydown' && event.key === 'Escape') ||
      (event.type === 'keyup' && !dialog.matches(':is(:focus, :focus-visible, :focus-within)'))
    ) {
      this.closeDialog();
    }
  };
}

customElements.define('product-hotspot-component', ProductHotspotComponent);
